import * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import { Icon } from '@gitbook/spine-icons';
import { Button, clipboard, toast } from '@gitbook/spine-react';

import { useBasicEditorValue } from '../BasicEditor';
import type { StreamAskInOrganizationResult } from './useStreamAskInOrganization';

/**
 * Toolbar for an answer. With actions to upvote/downvote/copy.
 */
export function AISearchAnswerToolbar(props: {
    query: string;
    answer: StreamAskInOrganizationResult;
    size?: 'small' | 'medium';
}): React.ReactElement | null {
    const { query, answer, size = 'small' } = props;
    const [rated, setRated] = React.useState(false);

    // If already parsed (markdown -> slate) we have answer.document
    const parsedDocument = 'document' in answer ? answer.document : undefined;
    // Otherwise, look for a document in the API response directly
    const apiAnswerDocument =
        answer.answer && 'document' in answer.answer ? answer.answer.document : undefined;

    // Only provide an initial document to the editor if we don't already have the parsed one
    const {
        value: { document: editorDocument },
    } = useBasicEditorValue(parsedDocument ? undefined : apiAnswerDocument);

    const finalDocument = parsedDocument ?? editorDocument;

    // Don't render if nothing usable
    if (!finalDocument.text) {
        return null;
    }

    const onPressCopy = () => {
        analytics.logEvent({ type: 'ai_search_copy_answer', query });
        clipboard.writeText(finalDocument.text);
    };

    const onRate = (type: 'up' | 'down') => {
        analytics.logEvent({
            type: type === 'up' ? 'ai_search_upvote_answer' : 'ai_search_downvote_answer',
            query,
        });
        setRated(true);
        toast('Thanks for your feedback!', {
            icon: type === 'up' ? Icon.ThumbsUp : Icon.ThumbsDown,
            description: 'This will help us improve the answering engine.',
        });
    };

    return (
        <div className="flex gap-2">
            <Button
                size={size}
                label="Copy answer"
                iconOnly
                leadingIcon={Icon.Copy}
                kind="blank"
                onPress={onPressCopy}
            />
            {!rated && (
                <>
                    <Button
                        size={size}
                        leadingIcon={Icon.ThumbsUp}
                        iconOnly
                        label="Mark as useful"
                        kind="blank"
                        onPress={() => onRate('up')}
                    />
                    <Button
                        size={size}
                        leadingIcon={Icon.ThumbsDown}
                        iconOnly
                        label="Mark as not relevant"
                        kind="blank"
                        onPress={() => onRate('down')}
                    />
                </>
            )}
        </div>
    );
}
